import { DateRangeSearch } from '@routes/__root'
import { environmentIdsSearchSchema } from '@routes/-environmentIdsSearch'
import { protectWithWorkspacePermissions } from '@routes/-protectWithPermission'
import { withCompanyName } from '@routes/-withCompanyName'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { VisitorsPage } from 'features/visitors'
import { PREDEFINED_DATE_RANGE_IDS } from 'helpers/dateRange'
import { getLDFlag } from 'helpers/launchDarkly'
import { canViewAnalyticsPermission } from 'hooks/usePermission'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { z } from 'zod/v4'

const visitorsSearchSchema = environmentIdsSearchSchema.merge(
  z.object({
    since: z.iso.datetime({ offset: true }).optional().catch(undefined),
    before: z.iso.datetime({ offset: true }).optional().catch(undefined),
    period: z.enum(PREDEFINED_DATE_RANGE_IDS).optional().catch(undefined),
  })
)

type VisitorsSearch = z.infer<typeof visitorsSearchSchema>

const supportedDateRanges = PREDEFINED_DATE_RANGE_IDS
type SupportedDateRangeId = (typeof supportedDateRanges)[number]

function enforceDateRangeBounds(
  search: VisitorsSearch & DateRangeSearch,
  clampToPeriod: SupportedDateRangeId = 'last_90_days'
): Partial<DateRangeSearch> {
  const adjustedSearch: Partial<DateRangeSearch> = {}

  if (search.period && !supportedDateRanges.includes(search.period as SupportedDateRangeId)) {
    adjustedSearch.period = clampToPeriod
  }

  if (!search.period && search.since && search.before) {
    const sinceDate = DateTime.fromISO(search.since)
    const beforeDate = DateTime.fromISO(search.before)
    const rangeInMonths = beforeDate.diff(sinceDate, 'months').months

    if (rangeInMonths > 3) {
      adjustedSearch.since = undefined
      adjustedSearch.before = undefined
      adjustedSearch.period = clampToPeriod
    }
  }

  return adjustedSearch
}

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/visitors/')({
  component: VisitorsPage,
  head: () => ({
    meta: [{ title: withCompanyName('Visitors') }],
  }),
  validateSearch: visitorsSearchSchema,
  beforeLoad: async (ctx) => {
    const { params, context, search } = ctx

    const flagEnabled = getLDFlag('dashboard.visitor-details.enabled', false, context.ldClient)
    if (!flagEnabled) {
      throw redirect({ to: AppRoute.SubscriptionOverview, params: { subscriptionId: params.subscriptionId } })
    }

    await protectWithWorkspacePermissions(canViewAnalyticsPermission)(ctx)

    const adjustedSearch = enforceDateRangeBounds(search as VisitorsSearch & DateRangeSearch)

    if (!isEmpty(adjustedSearch)) {
      throw redirect({
        to: '/workspaces/$subscriptionId/visitors',
        params,
        replace: true,
        search: (old) => ({
          ...old,
          ...adjustedSearch,
        }),
      })
    }
  },
})
